import { ReactNode, useMemo, useState } from 'react'
import { ArrowRightIcon, CheckIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { useHistory } from '../../../../providers/HistoryProvider'
import { TemplateObject } from '../../../../utils/TemplateObject'
import { BackButton } from '../../../common/Modal'
import { getNodeStatusColor } from '../../../../utils/MetagraphUtils'
import { RecordingTranscriptPairTitle } from '../../../transcript/RecordingTranscriptPairTitle'
import { RecordingTranscriptPairBlock } from '../../../transcript/RecordingTranscriptPairBlock'
import { ErrorBanner } from '../../../common/ErrorBanner'
import VetRecButton from '../../../common/VetRecButton'

interface SelectInputsForNotesProps {
    template: TemplateObject | undefined;
    selectedRecordings: string[];
    setSelectedRecordings: (x: string[]) => void;    
    primaryButtonText: string;
    handlePrimaryButtonClick: () => Promise<void>;
    onBack: () => void;
    primaryButtonIcon?: ReactNode;    
}

export const SelectInputsForNotes: React.FC<SelectInputsForNotesProps> = ({ 
    template,
    selectedRecordings,
    setSelectedRecordings,
    primaryButtonText,
    handlePrimaryButtonClick,
    onBack,
    primaryButtonIcon: generateButtonIcon = <SparklesIcon className="h-5 w-5" aria-hidden="true" />,
}) => {
    const {
        activeRecordingsAndTranscripts, 
        activeSession,
    } = useHistory();

    const [viewDetailsSelectedRecordingId, setViewDetailsSelectedRecordingId] = useState<string | undefined>(undefined);
    const [viewDetailsSelectedNdx, setViewDetailsSelectedNdx] = useState<number>(0);

    function handleViewRecordingDetails(recordingId: string, ndx: number) {
        setViewDetailsSelectedRecordingId(recordingId);
        setViewDetailsSelectedNdx(ndx);
    }

    function handleInputCheckboxChanged(recordingId: string) {
        if (selectedRecordings.includes(recordingId)) {
            setSelectedRecordings(selectedRecordings.filter(id => id !== recordingId));
        } else {
            setSelectedRecordings([...selectedRecordings, recordingId]);
        }
    }

    function handleOnBack() {
        if (viewDetailsSelectedRecordingId) {
            setViewDetailsSelectedRecordingId(undefined);
            return;
        } else {
            onBack();
        }
    }

    const anyRecordingsSelected = useMemo(() => selectedRecordings.length > 0, [selectedRecordings]);

    const selectedRecordingTranscriptPair = useMemo(() => {
        return viewDetailsSelectedRecordingId ? activeRecordingsAndTranscripts?.find(pair => pair.recording_id === viewDetailsSelectedRecordingId) : undefined
    }, [activeRecordingsAndTranscripts, viewDetailsSelectedRecordingId])

    return (
        <div>
            <BackButton onClick={handleOnBack} />
            {!viewDetailsSelectedRecordingId && <div className="flex flex-col gap-3 w-full">
                <div className="flex justify-center">
                    <div className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">
                            Selected template: <span {...(template ? {onClick: onBack, className: "font-semibold text-blue-600 cursor-pointer"} : {})}>{template?.name || 'None'}</span>
                        </p>
                    </div>
                </div>
                <div className="flex flex-col w-full">
                    {activeRecordingsAndTranscripts?.map((recordingTranscriptPair, ndx) => {
                        const isSelected = selectedRecordings.includes(recordingTranscriptPair.recording_id)
                        const color = isSelected ? getNodeStatusColor(recordingTranscriptPair.transcript_status) : 'gray'

                        return (
                        <div key={recordingTranscriptPair.recording_id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0 gap-x-2">
                            <div className="flex">
                                <button
                                    type="button"
                                    className={`flex items-center justify-center rounded-full h-6 w-6 ${
                                        isSelected 
                                            ? 'bg-blue-600 text-white' 
                                            : 'bg-white border-2 border-blue-600 hover:bg-blue-100'
                                    } transition-colors duration-200 ease-in-out`}
                                    onClick={() => handleInputCheckboxChanged(recordingTranscriptPair.recording_id)}
                                >
                                    {isSelected ? (
                                        <CheckIcon className="h-4 w-4" />
                                    ) : (
                                        <CheckIcon className="h-4 w-4 opacity-0 group-hover:opacity-30" />
                                    )}
                                </button>
                            </div>
                            <div
                                onClick={() => handleViewRecordingDetails(recordingTranscriptPair.recording_id, ndx)}
                                className={`flex w-full cursor-pointer justify-between items-center rounded-md bg-${color}-100 px-4 py-2 text-left text-sm font-semibold text-${color}-900 hover:bg-${color}-200 focus:outline-none focus-visible:ring focus-visible:ring-${color}-500/75 h-12`}>
                                <RecordingTranscriptPairTitle
                                    recordingTranscriptPair={recordingTranscriptPair}
                                    colorOverride={isSelected ? undefined : 'gray'}
                                />
                                <ArrowRightIcon className={`h-5 w-5 text-${color}-500 stroke-[3]`} />
                            </div>
                        </div>
                    )})}
                </div>
                <VetRecButton
                    type="button"
                    disabled={!anyRecordingsSelected}
                    onClick={handlePrimaryButtonClick}
                    showProcessing
                    processingText='Generating...'
                    className={`inline-flex w-full mt-3 items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm ${anyRecordingsSelected ? "bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600" : "bg-gray-600"} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                >
                    {generateButtonIcon}
                    {primaryButtonText}
                </VetRecButton>
            </div>}
            {viewDetailsSelectedRecordingId && <div>
                {selectedRecordingTranscriptPair ? <RecordingTranscriptPairBlock
                    recordingTranscriptPair={selectedRecordingTranscriptPair}
                    downloadFileName={(activeSession?.name ?? "recording")  + "=" + viewDetailsSelectedNdx.toString()}
                    defaultOpen={true}
                /> : <ErrorBanner errorText='There was an issue loading the recording. Please try again or reach out to support for assistance' />}
            </div>}
        </div>
    )
};